import { FileTextOutlined, PrinterOutlined } from '@ant-design/icons';
import { Button, Divider, Form, Row, Space, Table, Typography } from 'antd';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Fixed2, formatAmount } from '../../../../common/utils/common.utils';
import BreadCrumb from '../../../../components/common/breadCrumb/BreadCrumb';
import { DateInput, FormButton, FormInput } from '../../../../components/common/FormItem/FormItems';
import {
  SelectAccountList,
  SelectClients,
  SelectEmployee,
  SelectUsers,
} from '../../../../components/common/FormItem/SelectCustomFeilds';
import CommonViewReport from '../../../../components/common/Invoice/ViewInvoiceInfo/CommonViewReport';
import { landscapePageStyle } from '../../ledgers/pages/ClientLedgers';
import {
  useGetCollectionReportQuery,
  useLazyGetCollectionExcelQuery,
} from '../api/CollectionReportEndpoints';
import { BankSummaryColumn, CollectionColumn } from '../utils/CollectionColumn';

const { Text } = Typography;

type Props = {};

const CollectionReport = (props: Props) => {
  const [form] = useForm();
  const componentRef = useRef(null);

  const [info, setInfo] = useState<submitType>({
    client: '',
    employee_id: '',
    user_id: '',
    account_id: '',
    search: '',
    date_range: ['', ''],
  });

  const [fetchExcel] = useLazyGetCollectionExcelQuery();
  const { data, isLoading, isFetching } = useGetCollectionReportQuery({
    ...info,
    from_date: info?.date_range?.length && dayjs(info.date_range[0]).format('YYYY-MM-DD'),
    to_date: info?.date_range?.length && dayjs(info.date_range[1]).format('YYYY-MM-DD'),
  });

  const result = data?.data?.results;

  const onFinish = (values: submitType) => {
    setInfo(values);
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: landscapePageStyle,
  });
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    const daily = searchParams.get('daily');
    const fromDate = searchParams.get('from_date');
    const toDate = searchParams.get('to_date');

    if (daily) {
      form.setFieldValue('date_range', [dayjs(daily), dayjs(daily)]);
    } else if (fromDate && toDate) {
      form.setFieldValue('date_range', [dayjs(fromDate), dayjs(toDate)]);
    } else {
      form.setFieldValue('date_range', [dayjs(), dayjs()]);
    }
  }, []);

  const jsx = (
    <>
      <Divider orientation='left' plain>
        <Typography.Title style={{ fontFamily: 'sans-serif' }} level={5}>
          Collection
        </Typography.Title>
      </Divider>
      <Typography.Text>You have total: {data?.count} rows</Typography.Text>
      <Table
        dataSource={result}
        columns={CollectionColumn()}
        size='small'
        bordered
        pagination={false}
        summary={(_) => {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={8}>
                <div style={{ textAlign: 'right', marginRight: 10 }}>
                  <Typography.Text strong>Total Received Amount{'  '}</Typography.Text>
                </div>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={0} colSpan={1}>
                <div style={{ textAlign: 'right', marginRight: 5 }}>
                  <Typography.Text strong className='color-collection nowrap'>
                    {formatAmount(Fixed2(data?.data?.summary.total_amount))}
                  </Typography.Text>
                </div>
              </Table.Summary.Cell>

              <Table.Summary.Cell index={0} colSpan={2} />
            </Table.Summary.Row>
          );
        }}
      />

      <Divider orientation='left' plain>
        <Typography.Title style={{ fontFamily: 'sans-serif' }} level={5}>
          Bank wise summary
        </Typography.Title>
      </Divider>
      <div style={{ maxWidth: 850, margin: '0px auto' }}>
        <Table
          dataSource={data?.data?.bankWise.data}
          columns={BankSummaryColumn()}
          size='small'
          bordered
          pagination={false}
          summary={(_) => {
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <div style={{ textAlign: 'right', marginRight: 10 }}>
                    <Typography.Text strong>Total Received Amount{'  '}</Typography.Text>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={0} colSpan={1}>
                  <div style={{ textAlign: 'right', marginRight: 5 }}>
                    <Typography.Text strong className='nowrap'>
                      {formatAmount(Fixed2(data?.data?.bankWiseSummary.total_amount))}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </div>
      <Divider orientation='left' plain>
        <Typography.Title style={{ fontFamily: 'sans-serif' }} level={5}>
          Date wise summary
        </Typography.Title>
      </Divider>
      <div style={{ maxWidth: 850, margin: '0px auto' }}>
        <Table
          dataSource={data?.data?.dateWise.data}
          columns={[
            {
              title: 'SL.',
              render: (_, data, index) => <>{index + 1}</>,
            },
            {
              title: 'Date',
              render: (_, data) => <>{dayjs(data.receipt_date).format('DD-MMM-YYYY')}</>,
            },
            {
              title: 'Total Received Amount',
              align: 'right',
              render: (_, data) => <>{formatAmount(Fixed2(data.total_receipt))}</>,
            },
          ]}
          size='small'
          bordered
          pagination={false}
          summary={(_) => {
            const total = data?.data?.dateWise.data.reduce((acc, item) => acc + Number(item.total_receipt), 0);
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <div style={{ textAlign: 'right', marginRight: 10 }}>
                    <Typography.Text strong>Total of Date wise Received Amount{'  '}</Typography.Text>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={0} colSpan={1}>
                  <div style={{ textAlign: 'right', marginRight: 5 }}>
                    <Typography.Text strong className='nowrap'>
                      {formatAmount(Fixed2(total))}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </div>
    </>
  );

  const printExtraJsx = (
    <>
      <Row justify={'space-between'}>
        <Text>You have total :{data?.count} rows</Text>
        <Text>
          From date: {info?.date_range?.length && dayjs(info.date_range[0]).format('DD-MMM-YYYY')}
        </Text>
        <Text>
          To date: {info?.date_range?.length && dayjs(info.date_range[1]).format('DD-MMM-YYYY')}
        </Text>
        <Text>Search by: {info.search || 'N/A'}</Text>
      </Row>
    </>
  );

  const print_content = (
    <div hidden>
      <CommonViewReport
        children={jsx}
        printRef={componentRef}
        printExtraJsx={printExtraJsx}
        title={{
          info_title: '',
          title: 'Collection Report',
        }}
      />
    </div>
  );

  return (
    <>
      <BreadCrumb arrOfOption={['Report', 'Sales & Collection', 'Collection Report']} />
      <Space style={{ marginBottom: '1rem' }}>
        <Button type='primary' onClick={handlePrint}>
          <PrinterOutlined />
          Print
        </Button>
        <Button
          type='primary'
          onClick={() => {
            fetchExcel({
              ...info,
              from_date: info?.date_range?.length && dayjs(info.date_range[0]).format('YYYY-MM-DD'),
              to_date: info?.date_range?.length && dayjs(info.date_range[1]).format('YYYY-MM-DD'),
            });
          }}
        >
          <FileTextOutlined />
          Excel Report
        </Button>
      </Space>
      <Form
        layout='vertical'
        form={form}
        onFinish={onFinish}
        initialValues={{
          date_range: [dayjs(), dayjs()],
          client: 'all',
        }}
      >
        <Row gutter={16}>
          <SelectClients
            name='client'
            label='Select Client'
            placeholder='Select client'
            size={3}
            showAll
            offDropDown
          />
          <SelectAccountList name={'account_id'} label='Select Account' size={4} />

          <SelectEmployee name={'employee_id'} size={3} label='Received By' offDropDown />
          <SelectUsers name={'user_id'} size={3} label='Prepared By' offDropDown />

          <DateInput rangePicker label='Date Range' name='date_range' size={5} />
          <FormInput name={'search'} size={3} label='Search' />
          <FormButton
            label='Search'
            icon
            textAlign='left'
            size={3}
            loading={isLoading || isFetching}
          />
        </Row>
      </Form>

      {jsx}
      {print_content}
    </>
  );
};

export default CollectionReport;

export interface submitType {
  client: string;
  employee_id: number | string;
  user_id: number | string;
  account_id: string;
  search: string;
  date_range: [string, string];
}
